import { useEffect } from 'react';
import { RealtimeRole } from '@doxyme/realtime';
import { type MediaSettings } from '../../webrtc/media';
import { useAppDispatch, useAppSelector } from '../useReduxToolkit';
import { useLocalMedia, useLocalMediaActions } from '../useLocalMedia';
import { setIsAudioCall } from '../../features/apolloSession/apolloSession.slice';
import {
  getApolloSessionDetails,
  getIsAudioCall,
} from '../../features/apolloSession/apolloSession.selectors';
import { CallState } from '../../features/call/call.types';
import { useCallState } from '../useCallState';

type UseAudioCallReturnType = {
  disableVideoOnAudioOnly: () => Promise<void>;
  getMediaSettingsForAudioOnly: () => MediaSettings;
};

const getMediaSettingsForAudioOnly = (): MediaSettings => {
  return {
    video: false,
    audio: true,
  };
};

export const useAudioCall = (): UseAudioCallReturnType => {
  const dispatch = useAppDispatch();
  const isAudioCall = useAppSelector(getIsAudioCall);
  const { role } = useAppSelector(getApolloSessionDetails);
  const { hasVideoAccess, hasVideo } = useLocalMedia();
  const { toggleVideo } = useLocalMediaActions();
  const { callState } = useCallState();

  useEffect(() => {
    /**
     * Only provider can reset isAudioCall value by enabling their camera.
     * If patient turns on the camera during an audio call,
     * isAudioCall value should not be changed.
     * We should not reset isAudioCall on callStateInitializing
     * provider might have their camera turned on while starting the call which will
     * reset the flag. 'CallState.InProgress' check if to prevent that.
     */
    const shouldResetAudioCall =
      isAudioCall &&
      hasVideo &&
      role === RealtimeRole.Provider &&
      callState === CallState.InProgress;

    if (shouldResetAudioCall) {
      dispatch(setIsAudioCall(false));
    }
  }, [dispatch, isAudioCall, hasVideo, role]);

  const disableVideoOnAudioOnly = async (): Promise<void> => {
    if (isAudioCall) {
      const shouldToggleVideo = hasVideoAccess ? hasVideo : true;

      if (shouldToggleVideo) {
        await toggleVideo(false);
      }
    }
  };

  return {
    disableVideoOnAudioOnly,
    getMediaSettingsForAudioOnly,
  };
};
